import { gql } from "lib/apolloClient";
import {
  curriculumProgramFragment,
  attachmentFragment,
} from "modules/CommonFragments";

export const progressReportBaseTemplateFragment = {
  baseTemplateItemDetails: gql`
    fragment baseTemplateItemDetails on ProgressReportBaseTemplate {
      id
      name
      body
    }
  `,
  baseTemplateList: gql`
    fragment baseTemplateList on ProgressReportBaseTemplate {
      id
      name
    }
  `,
};

export const templateItemDetailFragment = {
  templateItemDetail: gql`
    fragment templateItem on PlannerTemplate {
      id
      body
      unitType
      name
      type
    }
  `,
};

export const organizationPresetFragment = {
  orgItem: gql`
    fragment organizationItem on OrganizationPreset {
      id
      name
      city
      ibCode
      country
      domains
      plannerFormat
      copyDataFromOrg
      isRostered
      isFree
      demoUnitPlanIds
    }
  `,
};

export const organizationFragment = {
  organisationFeedDetails: gql`
    fragment organisationFeedDetailsItem on Organization {
      id
      name
      logo
      code
      isPaid
      region
      currentAcademicYear {
        startDate
        endDate
      }
      organizationAnalytics
      curriculumPrograms {
        id
        type
        label
        acronym
        onboardingStatus
        platformId
        buddy {
          firstName
          lastName
          id
          email
        }
        curriculumProgramData {
          subscriptionPlan
          subscriptionStatus
          curriculumGroup
        }
        ... on IbDpCurriculumProgram {
          examSessionMonth
        }
        ... on UBDCurriculumProgram {
          variant
        }
        ...subscriptionPlanDetailsItem
      }
    }
    ${curriculumProgramFragment.subscriptionPlanDetails}
  `,
  organizationAttachmentDetails: gql`
    fragment organizationAttachmentDetailsItem on Organization {
      id
      attachments {
        ...attachmentBasicDetailsItem
      }
    }
    ${attachmentFragment.basicDetails}
  `,
  organizationDetails: gql`
    fragment organizationDetailsItem on Organization {
      id
      academicYears {
        id
        startDate
        endDate
        isCurrentAcademicYear
      }
      constants {
        examSessionMonth
        scheduler
      }
      curriculumPrograms(filters: $curriculumProgramFilters) {
        rosteringMethod
        id
        programDataLanguages
        dealId
        programType
        academicSetupType
        defaultLanguage
        academicProgram {
          id
        }
        ... on IbDpCurriculumProgram {
          examSessionMonth
        }
        accountProgram {
          id
        }
      }
      globalConstants(filter: $globalConstantsFilter) {
        id
        name
        value
      }
    }
  `,
  organizationList: gql`
    fragment organizationItem on Organization {
      id
      name
      logo
      code
      toddleBuddy
      isPaid
      region
      attachments {
        ...attachmentBasicDetailsItem
      }
      currentAcademicYear {
        startDate
        endDate
      }
      academicYears {
        id
        startDate
        endDate
        isCurrentAcademicYear
      }
      constants {
        examSessionMonth
        scheduler
        additionalData
      }
      courses {
        totalCount
      }
      staff {
        totalCount
      }
      students {
        totalCount
      }
      tier {
        plan
      }
      curriculumPrograms(filters: $curriculumProgramFilters) {
        id
        type
        rosteringMethod
        onboardingStatus
        label
        acronym
        dealId
        platformId
        academicSetupType
        programType
        buddy {
          firstName
          lastName
          id
        }
        ... on UBDCurriculumProgram {
          variant
        }
        ... on IbDpCurriculumProgram {
          examSessionMonth
        }
        ...subscriptionPlanDetailsItem
        onboardingSteps {
          step
          status
        }
        accountProgram {
          id
        }
        #This field is not used but fetched to avoid cache data loss for query getOrganizationDetails
        academicProgram {
          id
        }
        defaultLanguage
        programDataLanguages #This field is not used but fetched to avoid cache data loss for query getOrganizationDetails
        ...curriculumProgramBasicDetailsItem
      }
    }
    ${curriculumProgramFragment.subscriptionPlanDetails}
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
    ${attachmentFragment.basicDetails}
  `,
  organizationBasicDetails: gql`
    fragment organizationItem on Organization {
      id
      name
      logo
      code
      attachments {
        ...attachmentBasicDetailsItem
      }
    }
    ${attachmentFragment.basicDetails}
  `,
};

// User Feed

export const teacherFragments = {
  teacherFeed: gql`
    fragment teacherItem on Staff {
      id
      profileImage
      firstName
      lastName
      email
      role
      designation {
        id
        title
      }
      region
      status
      courses {
        id
        title
      }
      organization {
        id
        name
      }
    }
  `,
  teacherDetails: gql`
    fragment teacherItemDetail on Staff {
      id
      firstName
      lastName
      profileImage
      email
      designation {
        id
      }
      organization {
        id
        name
        tags {
          id
          key
          value
        }
      }
      entityTags {
        id
        key
        value
      }
    }
  `,
};

export const standardFragments = {
  standardFeed: gql`
    fragment standardItem on BenchmarkStandardSet {
      id
      title
      description
      globalSubjects
      curriculumName
      country
      state
      framework
      code
      curriculumTypes
      benchmarksCount
    }
  `,
  globalSubjects: gql`
    fragment globalSubjects on GlobalSubject {
      id
      title
      icon
      sequence
      type
      curriculumType
    }
  `,
};

export const academicYearFragment = {
  academicYear: gql`
    fragment academicYear on AcademicYear {
      id
      startDate
      endDate
      isCurrentAcademicYear
    }
  `,
};
